import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome, faSyncAlt } from '@fortawesome/free-solid-svg-icons'
import { Breadcrumb, Button, Card, Col, Form, Row, Spinner, Table } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import {
  cleanQueueJobs,
  fetchQueueJobs,
  fetchWorkerQueues,
  releaseQueueJob
} from '../../../controller/api/workerQueueApi'

const POLL_INTERVAL_MS = 4000

const DEFAULT_STATES = ['waiting', 'delayed', 'active', 'paused', 'prioritized']

const RELEASABLE = new Set(['wait', 'delayed', 'paused', 'prioritized'])

const STATE_OPTIONS = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'prioritized', label: 'Prioritized' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'waiting-children', label: 'Waiting children' }
]

function formatJobDataJson (value) {
  try {
    if (value === undefined || value === null) return '—'
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function stateBadgeVariant (state) {
  switch (state) {
    case 'completed':
      return 'success'
    case 'failed':
      return 'danger'
    case 'active':
      return 'primary'
    case 'delayed':
      return 'warning'
    case 'wait':
    case 'waiting':
      return 'secondary'
    case 'paused':
      return 'dark'
    case 'prioritized':
      return 'info'
    default:
      return 'secondary'
  }
}

export default function WorkerQueue () {
  const [cookies] = useCookies(['lottmekong'])
  const token = cookies.lottmekong

  const [queuesPayload, setQueuesPayload] = useState(null)
  const [selectedKey, setSelectedKey] = useState('calculate-reward')
  const [selectedStates, setSelectedStates] = useState(() => new Set(DEFAULT_STATES))
  const [start, setStart] = useState(0)
  const [end, setEnd] = useState(49)
  const [jobsPayload, setJobsPayload] = useState(null)
  const [loadingQueues, setLoadingQueues] = useState(false)
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [releasingId, setReleasingId] = useState(null)
  const [cleaningKind, setCleaningKind] = useState(null) // 'completed' | 'failed' | 'both'
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastUpdated, setLastUpdated] = useState(null)

  const loadQueuesRef = useRef(async () => {})
  const loadJobsRef = useRef(async () => {})

  const loadQueues = useCallback(async (opts = {}) => {
    const silent = opts.silent === true
    if (!token) return
    if (!silent) setLoadingQueues(true)
    try {
      const data = await fetchWorkerQueues(token)
      setQueuesPayload(data)
      if (data?.queues?.length) {
        setSelectedKey((prev) => (data.queues.some((q) => q.key === prev) ? prev : data.queues[0].key))
      }
      if (!silent) setLastUpdated(new Date())
    } catch (err) {
      if (!silent) {
        const msg = err.response?.data?.message || err.message || 'Không tải được danh sách queue'
        toast.error(msg)
      }
    } finally {
      if (!silent) setLoadingQueues(false)
    }
  }, [token])

  const statesParam = useMemo(() => {
    return Array.from(selectedStates).join(',')
  }, [selectedStates])

  const loadJobs = useCallback(async (opts = {}) => {
    const silent = opts.silent === true
    if (!token || !selectedKey) return
    if (!silent) setLoadingJobs(true)
    try {
      const data = await fetchQueueJobs(token, {
        queue: selectedKey,
        states: statesParam,
        start,
        end,
        asc: false
      })
      setJobsPayload(data)
      if (!silent) setLastUpdated(new Date())
    } catch (err) {
      if (!silent) {
        const msg = err.response?.data?.message || err.message || 'Không tải được job'
        toast.error(msg)
      }
    } finally {
      if (!silent) setLoadingJobs(false)
    }
  }, [token, selectedKey, statesParam, start, end])

  useEffect(() => {
    document.title = 'Hàng đợi worker'
  }, [])

  useEffect(() => {
    loadQueues()
  }, [loadQueues])

  useEffect(() => {
    loadJobs()
  }, [loadJobs])

  loadQueuesRef.current = loadQueues
  loadJobsRef.current = loadJobs

  useEffect(() => {
    if (!token || !autoRefresh) return undefined

    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return
      await loadQueuesRef.current({ silent: true })
      await loadJobsRef.current({ silent: true })
      setLastUpdated(new Date())
    }

    const id = setInterval(tick, POLL_INTERVAL_MS)
    const onVisible = () => {
      if (!document.hidden) {
        tick()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [token, autoRefresh])

  const toggleState = (value) => {
    setSelectedStates((prev) => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      if (next.size === 0) {
        return new Set(DEFAULT_STATES)
      }
      return next
    })
    setStart(0)
  }

  const selectedQueueMeta = queuesPayload?.queues?.find((q) => q.key === selectedKey)

  const onRelease = async (jobId, state) => {
    if (!RELEASABLE.has(state)) {
      toast.warn('Chỉ gỡ được job chưa chạy (waiting / delayed / paused / prioritized).')
      return
    }
    if (!window.confirm(`Gỡ job "${jobId}" khỏi hàng đợi?`)) {
      return
    }
    setReleasingId(jobId)
    try {
      const ok = await releaseQueueJob(token, selectedKey, jobId)
      if (ok) {
        toast.success('Đã gỡ job.')
        await loadJobs({ silent: true })
        await loadQueues({ silent: true })
        setLastUpdated(new Date())
      } else {
        toast.error('Gỡ job thất bại.')
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Gỡ job thất bại'
      toast.error(msg)
    } finally {
      setReleasingId(null)
    }
  }

  const onCleanFinishedJobs = async (kind) => {
    if (!token || !selectedKey) return
    const label =
      kind === 'completed'
        ? 'completed'
        : kind === 'failed'
          ? 'failed'
          : 'completed và failed'
    if (!window.confirm(`Xóa tất cả job ${label} trong queue "${selectedQueueMeta?.label ?? selectedKey}"? Thao tác không hoàn tác.`)) {
      return
    }
    setCleaningKind(kind)
    try {
      const completed = kind === 'completed' || kind === 'both'
      const failed = kind === 'failed' || kind === 'both'
      const data = await cleanQueueJobs(token, selectedKey, { completed, failed })
      if (data?.removed) {
        const { completed: c = 0, failed: f = 0 } = data.removed
        toast.success(`Đã xóa: completed ${c}, failed ${f}.`)
      } else {
        toast.error('Xóa job thất bại.')
      }
      await loadQueues({ silent: true })
      await loadJobs({ silent: true })
      setLastUpdated(new Date())
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Xóa job thất bại'
      toast.error(msg)
    } finally {
      setCleaningKind(null)
    }
  }

  return (
    <>
      <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-xl-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: 'breadcrumb-dark breadcrumb-transparent' }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item>Cài đặt</Breadcrumb.Item>
            <Breadcrumb.Item active>Hàng đợi worker</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <Card className="mb-4 border-0 shadow">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                await loadQueues()
                await loadJobs()
              }}
              disabled={loadingQueues || loadingJobs}
            >
              {(loadingQueues || loadingJobs) ? <Spinner animation="border" size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSyncAlt} className="me-1" />}
              Làm mới ngay
            </Button>
            <Form.Check
              type="switch"
              id="worker-queue-auto-refresh"
              className="mb-0"
              label={`Tự động làm mới (${POLL_INTERVAL_MS / 1000}s)`}
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            {lastUpdated && (
              <span className="small text-muted">
                Cập nhật lần cuối:{' '}
                <time dateTime={lastUpdated.toISOString()}>
                  {lastUpdated.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </time>
              </span>
            )}
          </div>

          {loadingQueues && !queuesPayload
            ? <Spinner animation="border" />
            : (
              <Row className="g-3">
                {(queuesPayload?.queues || []).map((q) => (
                  <Col key={q.key} md={4}>
                    <Card className={`h-100 ${q.key === selectedKey ? 'border-primary' : ''}`}>
                      <Card.Body className="py-3">
                        <div className="fw-bold mb-2">{q.label}</div>
                        <div className="small text-muted mb-2">{q.name}</div>
                        <div className="small">
                          {Object.entries(q.counts || {}).map(([k, v]) => (
                            <div key={k} className="d-flex justify-content-between">
                              <span>{k}</span>
                              <span className="fw-bold">{v}</span>
                            </div>
                          ))}
                        </div>
                        <Button
                          variant={q.key === selectedKey ? 'primary' : 'outline-primary'}
                          size="sm"
                          className="mt-2"
                          onClick={() => { setSelectedKey(q.key); setStart(0) }}
                        >
                          Xem job
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              )}
        </Card.Body>
      </Card>

      <Card className="mb-4 border-0 shadow">
        <Card.Body>
          <Card.Title className="mb-1">Danh sách job</Card.Title>
          {selectedQueueMeta && (
            <p className="text-muted small mb-3">
              Queue: <strong>{selectedQueueMeta.label}</strong>
              <span className="text-muted"> ({selectedQueueMeta.key})</span>
            </p>
          )}

          <div className="fw-bold mb-2">Lọc theo trạng thái</div>
          <p className="small text-muted mb-2">
            Chỉ tick các trạng thái cần xem. Nút <strong>Gỡ</strong> chỉ dùng được khi job còn trong hàng đợi (wait, delayed, paused, prioritized) — không gỡ được job đang chạy hoặc đã xong.
          </p>
          <div className="d-flex flex-wrap gap-3 mb-3">
            {STATE_OPTIONS.map((opt) => (
              <Form.Check
                key={opt.value}
                type="checkbox"
                id={`state-${opt.value}`}
                label={opt.label}
                checked={selectedStates.has(opt.value)}
                onChange={() => toggleState(opt.value)}
              />
            ))}
          </div>
          <Row className="g-2 align-items-end mb-3">
            <Col xs="auto">
              <Form.Label className="small mb-0">Vị trí bắt đầu</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={start}
                onChange={(e) => setStart(Math.max(0, parseInt(e.target.value, 10) || 0))}
                size="sm"
                style={{ width: 96 }}
              />
            </Col>
            <Col xs="auto">
              <Form.Label className="small mb-0">Vị trí kết thúc</Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={499}
                value={end}
                onChange={(e) => setEnd(Math.min(499, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                size="sm"
                style={{ width: 96 }}
              />
            </Col>
            <Col xs="auto">
              <Button size="sm" variant="secondary" onClick={loadJobs}>Tải job</Button>
            </Col>
            <Col xs="auto" className="border-start ps-3 ms-1">
              <div className="small text-muted mb-1">Dọn job đã xong / lỗi</div>
              <div className="d-flex flex-wrap gap-1">
                <Button
                  size="sm"
                  variant="outline-success"
                  disabled={!!cleaningKind || !selectedKey}
                  title="Xóa toàn bộ job completed trong queue đang chọn"
                  onClick={() => onCleanFinishedJobs('completed')}
                >
                  {cleaningKind === 'completed' ? <Spinner animation="border" size="sm" /> : null}
                  Xóa completed
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={!!cleaningKind || !selectedKey}
                  title="Xóa toàn bộ job failed trong queue đang chọn"
                  onClick={() => onCleanFinishedJobs('failed')}
                >
                  {cleaningKind === 'failed' ? <Spinner animation="border" size="sm" /> : null}
                  Xóa failed
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={!!cleaningKind || !selectedKey}
                  title="Xóa cả completed và failed"
                  onClick={() => onCleanFinishedJobs('both')}
                >
                  {cleaningKind === 'both' ? <Spinner animation="border" size="sm" /> : null}
                  Xóa cả hai
                </Button>
              </div>
            </Col>
          </Row>

          {loadingJobs
            ? <Spinner animation="border" />
            : (
              <div className="border rounded overflow-hidden">
                <div className="table-responsive">
                  <Table
                    hover
                    size="sm"
                    className="align-middle mb-0 worker-queue-job-table"
                    style={{ tableLayout: 'fixed', width: '100%' }}
                  >
                    <colgroup>
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '44%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <thead className="table-light">
                      <tr>
                        <th className="text-nowrap">ID</th>
                        <th>Trạng thái</th>
                        <th>Tên job</th>
                        <th>Dữ liệu (JSON)</th>
                        <th className="text-nowrap text-center">Lần thử</th>
                        <th className="text-end text-nowrap">Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(jobsPayload?.jobs || []).length === 0 && (
                        <tr><td colSpan={6} className="text-center text-muted py-4">Không có job trong khoảng này</td></tr>
                      )}
                      {(jobsPayload?.jobs || []).map((job) => {
                        const canRelease = RELEASABLE.has(job.state)
                        const attempts = job.attemptsMade ?? 0
                        const maxAtt = job.opts?.attempts
                        return (
                          <tr key={job.id}>
                            <td className="text-break small align-top py-2">{job.id}</td>
                            <td className="align-top py-2">
                              <span className={`badge bg-${stateBadgeVariant(job.state)}`}>{job.state}</span>
                            </td>
                            <td className="text-break small align-top py-2">{job.name}</td>
                            <td className="align-top py-2" style={{ verticalAlign: 'top' }}>
                              <pre
                                className="mb-0 rounded border bg-light text-dark"
                                style={{
                                  maxHeight: 120,
                                  overflow: 'auto',
                                  fontSize: '0.7rem',
                                  lineHeight: 1.35,
                                  padding: '0.5rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {formatJobDataJson(job.data)}
                              </pre>
                            </td>
                            <td className="text-center small align-middle text-nowrap">
                              {maxAtt != null ? `${attempts} / ${maxAtt}` : `${attempts}`}
                            </td>
                            <td className="text-end align-middle text-nowrap">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={!canRelease || releasingId === job.id}
                                title={canRelease ? 'Gỡ job khỏi hàng đợi' : 'Chỉ gỡ được job chưa chạy'}
                                onClick={() => onRelease(job.id, job.state)}
                              >
                                {releasingId === job.id ? <Spinner animation="border" size="sm" /> : 'Gỡ'}
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
              )}
        </Card.Body>
      </Card>
    </>
  )
}
